<template>
  <component
    :is="tag"
    v-bind="linkProps"
    class="ab-btn"
    :class="[
      `ab-btn--${variant}`,
      `ab-btn--${size}`,
      `ab-btn--${team}`,
      { 'ab-btn--icon-right': iconPosition === 'right', 'ab-btn--disabled': disabled }
    ]"
    :style="{ ...(variant === 'custom' ? { '--ab-custom-bg': customBg, '--ab-custom-hover-bg': customHoverBg, '--ab-custom-border': customBorder, '--ab-custom-hover-border': customHoverBorder, } : {}), ...(bold ? { fontWeight: 600 } : {}) }"
    :disabled="tag === 'button' ? disabled : undefined"
    :type="tag === 'button' ? nativeType : undefined"
    @click="handleClick"
  >
    <fa-icon v-if="icon" :icon="icon" class="ab-icon" :class="[`ab-icon--${size}`]" />
    <span class="ab-content"><slot /></span>
  </component>
</template>

<script>
export default {
  name: 'AppButton',
  props: {
    // Which design system team this belongs to
    team: {
      type: String,
      default: 'admin',
      validator: v => ['admin', 'staff', 'client'].includes(v)
    },
    // Visual variant
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'secondary', 'danger', 'ghost', 'custom'].includes(v)
    },
    customBg: { type: String, default: null },
    customHoverBg: { type: String, default: null },
    customBorder: { type: String, default: null },
    customHoverBorder: { type: String, default: null },
    // Font-awesome icon array e.g. ['fas', 'plus'] — omit to hide icon
    icon: {
      type: Array,
      default: null
    },
    // Icon placement
    iconPosition: {
      type: String,
      default: 'left',
      validator: v => ['left', 'right'].includes(v)
    },
    // Size
    size: {
      type: String,
      default: 'md',
      validator: v => ['xs', 'sm', 'md', 'lg'].includes(v)
    },
    // Action type
    // 'button'  — regular click action (default)
    // 'submit'  — submits a form
    // 'link'    — renders <a href>
    // 'route'   — renders <router-link :to>
    type: {
      type: String,
      default: 'button',
      validator: v => ['button', 'submit', 'link', 'route'].includes(v)
    },
    // href for type='link'
    href: { type: String, default: null },
    // to for type='route' (string or object)
    to: { type: [String, Object], default: null },
    // Disabled state
    disabled: { type: Boolean, default: false },
    bold: { type: Boolean, default: false }
  },
  computed: {
    tag () {
      if (this.type === 'link') return 'a'
      if (this.type === 'route') return 'router-link'
      return 'button'
    },
    nativeType () {
      return this.type === 'submit' ? 'submit' : 'button'
    },
    linkProps () {
      if (this.type === 'link') return { href: this.href, target: '_blank', rel: 'noopener noreferrer' }
      if (this.type === 'route') return { to: this.to }
      return {}
    }
  },
  methods: {
    handleClick (e) {
      if (this.disabled) { e.preventDefault(); return }
      this.$emit('click', e)
    }
  }
}
</script>

<style scoped>
/* ── Base ── */
.ab-btn {
  display: inline-flex; align-items: center; justify-content: center;
  gap: 7px; border: none; border-radius: 9999px; font-weight: 500;
  cursor: pointer; text-decoration: none; transition: all .15s;
  white-space: nowrap; line-height: 1;
  font-family: 'Open Sans UI', 'Open Sans', sans-serif;
}
.ab-btn--disabled { opacity: .5; cursor: not-allowed; }
.ab-btn--icon-right { flex-direction: row-reverse; }
.ab-icon { flex-shrink: 0; display: block; line-height: 0; }
.ab-content { display: inline-flex; align-items: center; gap: 5px; }

/* ── Sizes ── */
.ab-btn--xs { padding: 4px 14px;  font-size: 11px; gap: 4px; }
.ab-btn--sm { padding: 8px 16px;  font-size: 12px; }
.ab-btn--md { padding: 12px 32px;  font-size: 14px; min-width: 100px; }
.ab-btn--lg { padding: 12px 32px; font-size: 16px; min-width: 130px; }

.ab-icon--xs { font-size: 9px; margin-bottom: 1px;}

/* ── Admin team variants ── */
.ab-btn--admin.ab-btn--primary  { background: #b8860b; color: #fff;}
.ab-btn--admin.ab-btn--primary:hover  { background: #8b6508; }

.ab-btn--admin.ab-btn--secondary {
  background: #fff; color: #b8860b;
  border: 1.5px solid #b8860b; font-weight: 500;
}
.ab-btn--admin.ab-btn--secondary:hover { border-color: #8b6508; color: #8b6508; }

.ab-btn--admin.ab-btn--ghost {
  background: transparent; color: #64748b;
  border: 1.5px solid #e2e8f0;
}
.ab-btn--admin.ab-btn--ghost:hover { border-color: #8b6508; color: #b8860b; }

.ab-btn--admin.ab-btn--danger { background: #fee2e2; color: #dc2626; border: none; }
.ab-btn--admin.ab-btn--danger:hover { background: #fecaca; }

/* ── Staff team variants ── */
.ab-btn--staff.ab-btn--primary  { background: #1a3c6e; color: #fff; }
.ab-btn--staff.ab-btn--primary:hover  { background: #14305a; }

.ab-btn--staff.ab-btn--secondary {
  background: #f8fafc; color: #64748b;
  border: 1px dashed #9ca3af; font-weight: 600;
}
.ab-btn--staff.ab-btn--secondary:hover { border-color: #1a3c6e; color: #1a3c6e; background: #f1f5f9; }

.ab-btn--staff.ab-btn--ghost {
  background: transparent; color: #64748b;
  border: 1.5px solid #e2e8f0;
}
.ab-btn--staff.ab-btn--ghost:hover { border-color: #94a3b8; color: #334155; }

.ab-btn--staff.ab-btn--danger { background: #fee2e2; color: #dc2626; border: none; }
.ab-btn--staff.ab-btn--danger:hover { background: #fecaca; }

/* ── Client team variants ── */
.ab-btn--client.ab-btn--primary  { background: #1a3c6e; color: #fff; }
.ab-btn--client.ab-btn--primary:hover  { background: #14305a; }

.ab-btn--client.ab-btn--secondary {
  background: #fff; color: #64748b;
  border: 1.5px solid #dde3ee; font-weight: 500;
}
.ab-btn--client.ab-btn--secondary:hover { border-color: #1a3c6e; color: #1a3c6e; }

.ab-btn--client.ab-btn--ghost {
  background: transparent; color: #64748b;
  border: 1.5px solid #e2e8f0;
}
.ab-btn--client.ab-btn--ghost:hover { border-color: #94a3b8; color: #334155; }

.ab-btn--client.ab-btn--danger { background: #fee2e2; color: #dc2626; border: none; }
.ab-btn--client.ab-btn--danger:hover { background: #fecaca; }

/* ── Custom variant ── */
.ab-btn--custom { background: var(--ab-custom-bg, #64748b); color: #fff; border: var(--ab-custom-border, none); }
.ab-btn--custom:hover { background: var(--ab-custom-hover-bg, #475569); }
.ab-btn--custom { background: var(--ab-custom-bg, #64748b); color: #fff; border: var(--ab-custom-hover-border, none); }
</style>
